import { ChangeEventHandler } from 'react';

import { usePathContext } from '../../PathContext';
import { usePortalPostEditor } from '../../Post';
import { TranslationTable } from '@lifeserver/shared';

import { useSelectedLanguage } from '../../../hooks/selectedLanugage';

import { isRecord } from '../../../types/isRecord';

import styles from './TranslationTableInput.module.scss';

import { transformLinebreaks } from '../../portalUtils';
import { createTextKey } from '../../translationUtils';

export const isTranslationTable = (
    value: unknown,
): value is TranslationTable => {
    return (
        isRecord(value) &&
        '$type' in value &&
        value['$type'] === 'TranslationTable'
    );
};

export interface TranslationTableInputProps {
    value: TranslationTable;
    onChange?: (value: TranslationTable) => void;
}
export const TranslationTableInput = ({
    value,
    onChange,
}: TranslationTableInputProps) => {
    const { selectedLanguage } = useSelectedLanguage();
    const {
        postId,
        localizations,
        setLocalizations,
        postLocalizations,
        setPostLocalizations,
        localizationRecord,
    } = usePortalPostEditor();

    const { path } = usePathContext();
    const currentPath = createTextKey(path);
    const portalLocalizationId = value.$key ?? `${postId}.${currentPath}`;

    const currentTranslation = localizationRecord
        ? (localizationRecord[portalLocalizationId]?.[selectedLanguage] ??
          value[selectedLanguage] ??
          '')
        : '';

    const onInputChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
        const text = transformLinebreaks(e.target.value);
        const translationTable = {
            ...value,
            $type: 'TranslationTable' as const,
            $key: portalLocalizationId,
            [selectedLanguage]: text,
        };
        onChange?.(translationTable);
        const filtered = localizations.filter(
            (loc) =>
                !(
                    loc.id === portalLocalizationId &&
                    loc.language === selectedLanguage
                ),
        );
        const prevLoc = localizations.find(
            (loc) =>
                loc.id === portalLocalizationId &&
                loc.language === selectedLanguage,
        );
        setLocalizations([
            ...filtered,
            {
                id: portalLocalizationId,
                language: selectedLanguage,
                translation: text,
                metadataId: prevLoc?.metadataId ?? null,
            },
        ]);
        const exists = postLocalizations.some(
            (pl) =>
                pl.portalLocalizationId === portalLocalizationId &&
                pl.path === currentPath,
        );
        if (!exists) {
            setPostLocalizations([
                ...postLocalizations,
                {
                    portalLocalizationId: portalLocalizationId,
                    path: currentPath,
                    portalPostId: postId ?? '',
                },
            ]);
        }
    };
    return (
        <div style={{ position: 'relative' }}>
            <textarea
                className={styles.textarea}
                value={transformLinebreaks(currentTranslation)}
                onChange={onInputChange}
            />
        </div>
    );
};
